import React, { useState, useEffect, useMemo } from 'react';
import { Plus, X, RefreshCw, ChevronLeft, ChevronRight, Clock, Check, XCircle, Info, Sun, HeartPulse, HeartHandshake, Zap, Baby, Users, CalendarDays, type LucideIcon } from 'lucide-react';
import { holidayRequestApi, blockedDateApi, employeeApi, employeeLeaveBalanceApi } from '../services/api';
import { HolidayRequest, CreateHolidayRequestRequest, BlockedDate, EmployeeLeaveTypeInfo, EmployeeLeaveBalance } from '../types';
import { useAuth } from '../context/AuthContext';
import { appConfirm } from '../context/SystemDialogContext';
import { formatCompanyDate, formatCompanyDateLocale } from '../utils/companyDateFormat';
import { extractDatePart } from '../utils/datetime';
import { format, startOfMonth, endOfMonth, addMonths, subMonths, eachDayOfInterval, isSameMonth, isSameDay } from 'date-fns';
import {
  formatLeaveBalanceAmount,
  getDisplayAccrued,
  getDisplayCarriedForward,
  getDisplayPending,
  getDisplayRemaining,
  getDisplayTaken,
  leaveBalanceUnit,
} from '../utils/leaveBalanceDisplay';

function isAnnualLeaveName(name?: string | null): boolean {
  return !!name && /annual/i.test(name.trim());
}

function leaveTypeIcon(name: string): LucideIcon {
  const n = name.toLowerCase();
  if (/annual|holiday|vacation/.test(n)) return Sun;
  if (/sick/.test(n)) return HeartPulse;
  if (/bereav/.test(n)) return HeartHandshake;
  if (/force\s*majeure|emergency/.test(n)) return Zap;
  if (/maternity/.test(n)) return Baby;
  if (/paternity/.test(n)) return Baby;
  if (/parent/.test(n)) return Users;
  return CalendarDays;
}

/** Distinct light chip colors per leave type (border / bg / text). */
function leaveTypeChipTone(name: string, index: number): string {
  const n = name.toLowerCase();
  if (/annual|holiday|vacation/.test(n)) return 'border-sky-200 bg-sky-50 text-sky-800';
  if (/sick/.test(n)) return 'border-rose-200 bg-rose-50 text-rose-800';
  if (/bereav/.test(n)) return 'border-violet-200 bg-violet-50 text-violet-800';
  if (/force\s*majeure|emergency/.test(n)) return 'border-amber-200 bg-amber-50 text-amber-800';
  if (/maternity/.test(n)) return 'border-pink-200 bg-pink-50 text-pink-800';
  if (/paternity/.test(n)) return 'border-indigo-200 bg-indigo-50 text-indigo-800';
  if (/parent/.test(n)) return 'border-teal-200 bg-teal-50 text-teal-800';
  if (/unpaid|day\s*off/.test(n)) return 'border-stone-200 bg-stone-50 text-stone-700';
  const fallback = [
    'border-cyan-200 bg-cyan-50 text-cyan-800',
    'border-lime-200 bg-lime-50 text-lime-800',
    'border-orange-200 bg-orange-50 text-orange-800',
    'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-800',
    'border-emerald-200 bg-emerald-50 text-emerald-800',
  ];
  return fallback[index % fallback.length];
}

/** Local calendar day as yyyy-MM-dd (no UTC shift). */
function localDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseDateOnlyLocal(value: string): Date | null {
  const key = extractDatePart(value);
  if (!key) return null;
  const [y, m, d] = key.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

const MyHolidays: React.FC = () => {
  const { employee } = useAuth();
  const [userHolidays, setUserHolidays] = useState<HolidayRequest[]>([]);
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [statusPopup, setStatusPopup] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateHolidayRequestRequest>({
    employeeId: 0,
    managerSupervisor: '',
    venueDepartment: '',
    startDate: '',
    endDate: '',
    totalHours: undefined,
    reason: '',
    status: 'Pending',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [holidayTypes, setHolidayTypes] = useState<Record<string, string>>({});
  const [detailsPopupHoliday, setDetailsPopupHoliday] = useState<HolidayRequest | null>(null);
  const [employeeLeaveTypes, setEmployeeLeaveTypes] = useState<EmployeeLeaveTypeInfo[]>([]);
  const [leaveBalances, setLeaveBalances] = useState<EmployeeLeaveBalance[]>([]);
  const [activeTab, setActiveTab] = useState<'calendar' | 'balances' | 'status'>('calendar');
  const [scheduleCoverageByDate, setScheduleCoverageByDate] = useState<
    Record<
      string,
      { scheduleId: number; scheduleName: string; count: number; pending: number; approved: number }[]
    >
  >({});
  const [scheduleMorePopup, setScheduleMorePopup] = useState<string | null>(null);
  const [scheduleCoverageTick, setScheduleCoverageTick] = useState(0);

  const generateDateRange = (startDate: string, endDate: string): string[] => {
    if (!startDate || !endDate) return [];
    const start = parseDateOnlyLocal(startDate);
    const end = parseDateOnlyLocal(endDate);
    if (!start || !end || end < start) return [];
    const dates: string[] = [];
    const current = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    while (current <= end) {
      dates.push(localDateKey(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      const dates = generateDateRange(formData.startDate, formData.endDate);
      setHolidayTypes(prev => {
        const newHolidayTypes: Record<string, string> = {};
        dates.forEach(date => { if (prev[date]) newHolidayTypes[date] = prev[date]; });
        return newHolidayTypes;
      });
    } else {
      setHolidayTypes({});
    }
  }, [formData.startDate, formData.endDate]);

  const loadHolidays = async () => {
    try {
      setLoading(true);
      const currentYear = new Date().getFullYear();
      const [allHolidays, blocked, leaveTypes, balances] = await Promise.all([
        holidayRequestApi.getAll(employee?.id),
        blockedDateApi.getAll(),
        employee?.id ? employeeApi.getLeaveTypes(employee.id, currentYear).catch(() => []) : Promise.resolve([]),
        employee?.id ? employeeLeaveBalanceApi.getAll(employee.id, currentYear).catch(() => []) : Promise.resolve([]),
      ]);
      setUserHolidays(allHolidays || []);
      setBlockedDates(blocked || []);
      setEmployeeLeaveTypes(leaveTypes || []);
      setLeaveBalances(balances || []);
      setScheduleCoverageTick((t) => t + 1);
    } catch (error) {
      console.error('Error loading holidays:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadHolidays();
  }, [employee?.id]);

  useEffect(() => {
    if (!employee?.id) {
      setScheduleCoverageByDate({});
      return;
    }
    let cancelled = false;
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    holidayRequestApi
      .getMyScheduleCoverage(localDateKey(monthStart), localDateKey(monthEnd))
      .then((res) => {
        if (cancelled) return;
        const map: Record<
          string,
          { scheduleId: number; scheduleName: string; count: number; pending: number; approved: number }[]
        > = {};
        (res.days || []).forEach((day) => {
          map[day.date] = day.schedules || [];
        });
        setScheduleCoverageByDate(map);
      })
      .catch((err) => {
        console.error('Error loading schedule leave coverage:', err);
        if (!cancelled) setScheduleCoverageByDate({});
      });
    return () => {
      cancelled = true;
    };
  }, [employee?.id, currentMonth, scheduleCoverageTick]);

  const leaveBalanceCards = useMemo(() => {
    if (employeeLeaveTypes.length > 0) {
      return employeeLeaveTypes.map((lt) => {
        const bal = leaveBalances.find((b) => b.leaveTypeId === lt.leaveTypeId);
        const annual = !!lt.isAnnualLeave || isAnnualLeaveName(lt.name);
        const unit = bal ? leaveBalanceUnit(bal) : 'days';
        const accrued =
          lt.accruedDays != null
            ? lt.accruedDays
            : bal
              ? getDisplayAccrued(bal)
              : 0;
        return {
          id: lt.leaveTypeId,
          name: lt.name,
          isAnnualLeave: annual,
          unit,
          accrued,
          used: lt.takenDays,
          pending: lt.pendingDays,
          available: lt.remainingDays,
          carriedForward: lt.carriedForwardDays ?? (bal ? getDisplayCarriedForward(bal) : 0),
        };
      });
    }

    return leaveBalances.map((b) => {
      const name = b.leaveType?.name || 'Leave';
      const unit = leaveBalanceUnit(b);
      return {
        id: b.id,
        name,
        isAnnualLeave: isAnnualLeaveName(name),
        unit,
        accrued: getDisplayAccrued(b),
        used: getDisplayTaken(b),
        pending: getDisplayPending(b),
        available: getDisplayRemaining(b),
        carriedForward: getDisplayCarriedForward(b),
      };
    });
  }, [employeeLeaveTypes, leaveBalances]);

  const onRefresh = () => {
    setRefreshing(true);
    loadHolidays();
  };

  const isDateBlocked = (startDate: string, endDate: string): boolean => {
    const start = extractDatePart(startDate);
    const end = extractDatePart(endDate);
    if (!start || !end) return false;
    return blockedDates.some((bd) => {
      const bdStart = extractDatePart(bd.startDate);
      const bdEnd = extractDatePart(bd.endDate);
      if (!bdStart || !bdEnd) return false;
      return (
        (bdStart <= start && bdEnd >= start) ||
        (bdStart <= end && bdEnd >= end) ||
        (bdStart >= start && bdEnd <= end)
      );
    });
  };

  const handleAddHoliday = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!employee?.id) return;

    const errors: Record<string, string> = {};
    if (!formData.startDate) errors.startDate = 'Start date is required';
    if (!formData.endDate) errors.endDate = 'End date is required';
    if (formData.startDate && formData.endDate && new Date(formData.endDate) < new Date(formData.startDate)) {
      errors.endDate = 'End date must be after or equal to start date';
    }
    if (!formData.managerSupervisor?.trim()) errors.managerSupervisor = 'Manager/Supervisor is required';
    if (!formData.venueDepartment?.trim()) errors.venueDepartment = 'Venue/Department is required';
    if (!formData.reason?.trim()) errors.reason = 'Reason is required';

    if (formData.startDate && formData.endDate && isDateBlocked(formData.startDate, formData.endDate)) {
      errors.startDate = 'Selected date range is blocked';
    }

    if (formData.startDate && formData.endDate) {
      const dates = generateDateRange(formData.startDate, formData.endDate);
      const missing = dates.filter(d => !holidayTypes[d] || !holidayTypes[d].trim());
      if (missing.length > 0) {
        errors.holidayTypes = 'Holiday type is required for all days';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setSubmitting(true);
      const holidayTypesArray = formData.startDate && formData.endDate
        ? generateDateRange(formData.startDate, formData.endDate)
            .sort()
            .map(date => ({ date, holidayType: holidayTypes[date] || '' }))
        : undefined;
      const totalHours =
        formData.totalHours != null && formData.totalHours > 0 ? formData.totalHours : undefined;
      await holidayRequestApi.create({
        ...formData,
        employeeId: employee.id,
        totalHours,
        holidayTypes: holidayTypesArray,
      });
      const leaveMonth = parseDateOnlyLocal(formData.startDate);
      if (leaveMonth) setCurrentMonth(leaveMonth);
      setActiveTab('calendar');
      await loadHolidays();
      setShowAddModal(false);
      setFormData({
        employeeId: 0,
        managerSupervisor: '',
        venueDepartment: '',
        startDate: '',
        endDate: '',
        totalHours: undefined,
        reason: '',
        status: 'Pending',
      });
      setHolidayTypes({});
      setFormErrors({});
    } catch (error: any) {
      alert(error?.response?.data || error?.message || 'Failed to create holiday request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelHoliday = async (holiday: HolidayRequest) => {
    if (!(await appConfirm(`Are you sure you want to cancel your holiday request from ${holiday.startDate} to ${holiday.endDate}?`, { danger: true, confirmLabel: 'Cancel' }))) return;
    try {
      await holidayRequestApi.cancel(holiday);
      await loadHolidays();
    } catch (error: any) {
      alert(error?.response?.data || error?.message || 'Failed to cancel holiday');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Approved': return 'bg-green-50 text-green-700 border-green-200';
      case 'Pending': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Denied': return 'bg-red-50 text-red-700 border-red-200';
      case 'Cancelled': return 'bg-neutral-50 text-neutral-600 border-neutral-200';
      default: return 'bg-neutral-50 text-neutral-700 border-neutral-200';
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date: Date) => {
    const start = startOfMonth(date);
    const end = endOfMonth(date);
    const days = eachDayOfInterval({ start, end });
    const startDayOfWeek = start.getDay();
    const leadingEmpty = Array(startDayOfWeek).fill(null);
    return [...leadingEmpty, ...days];
  };

  const getHolidaysForDate = (date: Date | null): HolidayRequest[] => {
    if (!date) return [];
    const dateStr = localDateKey(date);
    return userHolidays.filter((hr) => {
      const start = extractDatePart(hr.startDate);
      const end = extractDatePart(hr.endDate);
      if (start && end) {
        return dateStr >= start && dateStr <= end;
      }
      // Fallback when start/end missing: match stored day entries
      return (hr.holidayTypes ?? []).some((d) => extractDatePart(d.date) === dateStr);
    });
  };

  const getFilteredHolidaysForDate = (date: Date | null): HolidayRequest[] => getHolidaysForDate(date);

  const isDayBlocked = (date: Date | null): boolean => {
    if (!date) return false;
    const dateStr = localDateKey(date);
    return blockedDates.some((bd) => {
      const bdStart = extractDatePart(bd.startDate);
      const bdEnd = extractDatePart(bd.endDate);
      if (!bdStart || !bdEnd) return false;
      return dateStr >= bdStart && dateStr <= bdEnd;
    });
  };

  const pendingHolidays = userHolidays.filter(h => h.status === 'Pending');
  const approvedHolidays = userHolidays.filter(h => h.status === 'Approved');
  const deniedHolidays = userHolidays.filter(h => h.status === 'Denied');
  const cancelledHolidays = userHolidays.filter(h => h.status === 'Cancelled');

  const leaveStatusSummary = [
    {
      status: 'Pending',
      count: pendingHolidays.length,
      holidays: pendingHolidays,
      icon: Clock,
      chip: 'border-amber-200 bg-amber-50 text-amber-800',
    },
    {
      status: 'Approved',
      count: approvedHolidays.length,
      holidays: approvedHolidays,
      icon: Check,
      chip: 'border-green-200 bg-green-50 text-green-800',
    },
    {
      status: 'Denied',
      count: deniedHolidays.length,
      holidays: deniedHolidays,
      icon: XCircle,
      chip: 'border-red-200 bg-red-50 text-red-800',
    },
    {
      status: 'Cancelled',
      count: cancelledHolidays.length,
      holidays: cancelledHolidays,
      icon: X,
      chip: 'border-neutral-200 bg-neutral-50 text-neutral-700',
    },
  ] as const;

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const days = getDaysInMonth(currentMonth);

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-neutral-800">My Holidays</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="p-2 rounded-lg bg-neutral-100 hover:bg-neutral-200 disabled:opacity-50"
          >
            <RefreshCw className={`h-5 w-5 text-blue-600 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add Holiday
          </button>
        </div>
      </div>

      <div className="border-b border-neutral-200">
        <nav className="flex flex-wrap gap-1 sm:gap-6" aria-label="My Holidays sections">
          {(
            [
              { id: 'calendar' as const, label: 'Calendar' },
              { id: 'balances' as const, label: 'Your Leave Balances' },
              { id: 'status' as const, label: 'Holiday Status' },
            ] as const
          ).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
              }`}
            >
              {tab.label}
              {tab.id === 'status' && (
                <span className="ml-1.5 text-xs font-semibold text-neutral-500">
                  ({leaveStatusSummary.reduce((sum, s) => sum + s.count, 0)})
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-neutral-500">Loading holidays...</div>
        </div>
      ) : (
        <>
          {activeTab === 'balances' && (
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
              {leaveBalanceCards.length > 0 ? (
                <>
                  <div className="flex flex-wrap items-center gap-1.5 mb-4">
                    {leaveBalanceCards.map((lt, index) => {
                      const Icon = leaveTypeIcon(lt.name);
                      return (
                        <span
                          key={lt.id}
                          title={`${lt.name}: ${formatLeaveBalanceAmount(lt.available, lt.unit)} available`}
                          className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs font-semibold tabular-nums ${leaveTypeChipTone(lt.name, index)}`}
                        >
                          <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
                          <span className="sr-only">{lt.name} available </span>
                          {formatLeaveBalanceAmount(lt.available, lt.unit)}
                        </span>
                      );
                    })}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-neutral-200 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">
                          <th className="py-2 pr-3 font-medium">Leave type</th>
                          <th className="py-2 px-2 font-medium text-right">Accrued</th>
                          <th className="py-2 px-2 font-medium text-right">Used</th>
                          <th className="py-2 px-2 font-medium text-right">Pending</th>
                          <th className="py-2 pl-2 font-medium text-right">Available</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leaveBalanceCards.map((lt) => (
                          <tr
                            key={lt.id}
                            className={`border-b border-neutral-100 last:border-0 ${
                              lt.isAnnualLeave ? 'bg-blue-50/60' : ''
                            }`}
                          >
                            <td className="py-2 pr-3 font-medium text-neutral-800">
                              {lt.name}
                              {lt.isAnnualLeave ? ' (Annual)' : ''}
                              {lt.carriedForward > 0 && (
                                <span className="block text-xs font-normal text-neutral-500">
                                  CF: {formatLeaveBalanceAmount(lt.carriedForward, lt.unit)}
                                </span>
                              )}
                            </td>
                            <td className="py-2 px-2 text-right tabular-nums text-neutral-800">
                              {formatLeaveBalanceAmount(lt.accrued, lt.unit)}
                            </td>
                            <td className="py-2 px-2 text-right tabular-nums text-orange-600">
                              {formatLeaveBalanceAmount(lt.used, lt.unit)}
                            </td>
                            <td className="py-2 px-2 text-right tabular-nums text-amber-600">
                              {formatLeaveBalanceAmount(lt.pending, lt.unit)}
                            </td>
                            <td className="py-2 pl-2 text-right tabular-nums font-semibold text-blue-600">
                              {formatLeaveBalanceAmount(lt.available, lt.unit)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <p className="text-sm text-neutral-500">
                  No leave balances are available yet. Ask your administrator to assign a leave policy to your profile.
                </p>
              )}
            </div>
          )}

          {activeTab === 'status' && (
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
              <div className="flex flex-wrap items-center gap-1.5 mb-4">
                {leaveStatusSummary.map(({ status, count, icon: Icon, chip }) => (
                  <button
                    key={status}
                    type="button"
                    title={`${status}: ${count}`}
                    onClick={() => setStatusPopup(status)}
                    className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs font-semibold tabular-nums hover:opacity-90 ${chip}`}
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
                    <span>{status}</span>
                    <span className="tabular-nums">{count}</span>
                  </button>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-200 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">
                      <th className="py-2 pr-3 font-medium">Status</th>
                      <th className="py-2 pl-2 font-medium text-right">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaveStatusSummary.map(({ status, count, icon: Icon, chip }) => (
                      <tr key={status} className="border-b border-neutral-100 last:border-0">
                        <td className="py-2 pr-3">
                          <button
                            type="button"
                            onClick={() => setStatusPopup(status)}
                            className="inline-flex items-center gap-2 font-medium text-neutral-800 hover:text-blue-700"
                          >
                            <span className={`inline-flex rounded-md border p-1 ${chip}`}>
                              <Icon className="h-3.5 w-3.5" aria-hidden />
                            </span>
                            {status}
                          </button>
                        </td>
                        <td className="py-2 pl-2 text-right">
                          <button
                            type="button"
                            onClick={() => setStatusPopup(status)}
                            className="tabular-nums font-semibold text-neutral-800 hover:text-blue-700"
                          >
                            {count}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'calendar' && (
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-neutral-800">Calendar</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  className="p-2 rounded-lg hover:bg-neutral-100"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <span className="min-w-[140px] text-center font-medium">
                  {format(currentMonth, 'MMMM yyyy')}
                </span>
                <button
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  className="p-2 rounded-lg hover:bg-neutral-100"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1">
              {dayNames.map(day => (
                <div key={day} className="p-2 text-center text-xs font-medium text-neutral-500">
                  {day}
                </div>
              ))}
              {days.map((date, index) => {
                const isCurrentMonth = date && isSameMonth(date, currentMonth);
                const isToday = date && isSameDay(date, new Date());
                const holidays = getFilteredHolidaysForDate(date);
                const blocked = date && isDayBlocked(date);
                const dateKey = date ? localDateKey(date) : '';
                const scheduleCounts = dateKey ? scheduleCoverageByDate[dateKey] || [] : [];
                return (
                  <div
                    key={index}
                    className={`min-h-[80px] p-1 border rounded text-sm ${
                      !date ? 'bg-neutral-50 border-transparent' :
                      blocked ? 'bg-red-50 border-red-200' :
                      isToday ? 'bg-blue-50 border-blue-300' : 'bg-white border-neutral-200'
                    }`}
                  >
                    {date && (
                      <>
                        <div className={`font-medium ${!isCurrentMonth ? 'text-neutral-300' : blocked ? 'text-red-600' : isToday ? 'text-blue-600' : 'text-neutral-900'}`}>
                          {date.getDate()}
                        </div>
                        {blocked && (
                          <div className="mt-1 text-xs text-red-600">Blocked</div>
                        )}
                        {holidays.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {holidays.slice(0, 2).map(hr => (
                              <button
                                key={hr.id}
                                type="button"
                                title={`${hr.status}${hr.holidayTypes?.[0]?.holidayType ? ` · ${hr.holidayTypes[0].holidayType}` : ''}`}
                                onClick={() => setStatusPopup(hr.status)}
                                className={`block w-full text-left text-xs px-1 py-0.5 rounded truncate border ${getStatusColor(hr.status)}`}
                              >
                                {hr.status}
                              </button>
                            ))}
                            {holidays.length > 2 && <div className="text-xs text-neutral-500">+{holidays.length - 2}</div>}
                          </div>
                        )}
                        {scheduleCounts.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {scheduleCounts.slice(0, 2).map((s) => (
                              <div
                                key={s.scheduleId}
                                title={`${s.scheduleName}: ${s.count} off (${s.approved} approved, ${s.pending} pending)`}
                                className="flex items-center gap-1 text-[11px] text-neutral-600 truncate"
                              >
                                <CalendarDays className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
                                <span className="truncate">{s.scheduleName}: {s.count}</span>
                              </div>
                            ))}
                            {scheduleCounts.length > 2 && (
                              <button
                                type="button"
                                onClick={() => setScheduleMorePopup(dateKey)}
                                className="text-[11px] font-medium text-blue-600 hover:underline"
                              >
                                More ({scheduleCounts.length - 2})
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          )}
        </>
      )}

      {/* Add Holiday Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-neutral-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-neutral-800">Add Holiday</h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setFormData({
                    employeeId: 0,
                    managerSupervisor: '',
                    venueDepartment: '',
                    startDate: '',
                    endDate: '',
                    totalHours: undefined,
                    reason: '',
                    status: 'Pending',
                  });
                  setHolidayTypes({});
                  setFormErrors({});
                }}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleAddHoliday} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Start Date *</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-md ${formErrors.startDate ? 'border-red-500' : 'border-neutral-300'}`}
                />
                {formErrors.startDate && <p className="mt-1 text-sm text-red-600">{formErrors.startDate}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">End Date *</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-md ${formErrors.endDate ? 'border-red-500' : 'border-neutral-300'}`}
                />
                {formErrors.endDate && <p className="mt-1 text-sm text-red-600">{formErrors.endDate}</p>}
              </div>
              {formData.startDate && formData.endDate && generateDateRange(formData.startDate, formData.endDate).length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Holiday Type *</label>
                  <p className="text-xs text-neutral-500 mb-2">Select holiday type for each day:</p>
                  {formErrors.holidayTypes && <p className="mb-2 text-sm text-red-600">{formErrors.holidayTypes}</p>}
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {generateDateRange(formData.startDate, formData.endDate).map(date => (
                      <div key={date} className="flex items-center gap-3">
                        <span className="text-sm text-neutral-700 min-w-[140px]">
                          {formatCompanyDateLocale(date + 'T00:00:00', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                        <select
                          value={holidayTypes[date] || ''}
                          onChange={e => {
                            setHolidayTypes({ ...holidayTypes, [date]: e.target.value });
                            if (formErrors.holidayTypes) setFormErrors({ ...formErrors, holidayTypes: '' });
                          }}
                          className={`flex-1 px-3 py-2 border rounded-md text-sm ${
                            (!holidayTypes[date] || !holidayTypes[date].trim()) && formErrors.holidayTypes
                              ? 'border-red-500' : 'border-neutral-300'
                          }`}
                        >
                          <option value="">Select type...</option>
                          {employeeLeaveTypes.length > 0 ? (
                            employeeLeaveTypes.map(lt => (
                              <option key={lt.leaveTypeId} value={lt.name}>
                                {lt.name} ({lt.takenDays} used, {lt.pendingDays} pending, {lt.remainingDays} available
                                {lt.accruedDays != null ? `, ${lt.accruedDays} accrued` : ''})
                              </option>
                            ))
                          ) : leaveBalanceCards.length > 0 ? (
                            leaveBalanceCards.map(lt => (
                              <option key={lt.id} value={lt.name}>
                                {lt.name} ({lt.used} used, {lt.pending} pending, {lt.available} available, {lt.accrued} accrued)
                              </option>
                            ))
                          ) : (
                            <>
                              <option value="Paid Holiday">Paid Holiday</option>
                              <option value="Day Off">Day Off</option>
                            </>
                          )}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Total hours (optional)</label>
                <input
                  type="number"
                  min={0}
                  step={0.25}
                  value={formData.totalHours ?? ''}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      totalHours: e.target.value === '' ? undefined : parseFloat(e.target.value),
                    })
                  }
                  placeholder="e.g. 12 — deduct/pay these hours when approved"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md"
                />
                <p className="mt-1 text-xs text-neutral-500">
                  For hourly leave (e.g. nurses). Leave blank to use day-based counting.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Manager/Supervisor *</label>
                <input
                  type="text"
                  value={formData.managerSupervisor}
                  onChange={e => setFormData({ ...formData, managerSupervisor: e.target.value })}
                  placeholder="Enter manager/supervisor name"
                  className={`w-full px-3 py-2 border rounded-md ${formErrors.managerSupervisor ? 'border-red-500' : 'border-neutral-300'}`}
                />
                {formErrors.managerSupervisor && <p className="mt-1 text-sm text-red-600">{formErrors.managerSupervisor}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Venue/Department *</label>
                <input
                  type="text"
                  value={formData.venueDepartment}
                  onChange={e => setFormData({ ...formData, venueDepartment: e.target.value })}
                  placeholder="Enter venue/department"
                  className={`w-full px-3 py-2 border rounded-md ${formErrors.venueDepartment ? 'border-red-500' : 'border-neutral-300'}`}
                />
                {formErrors.venueDepartment && <p className="mt-1 text-sm text-red-600">{formErrors.venueDepartment}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Reason *</label>
                <textarea
                  value={formData.reason || ''}
                  onChange={e => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Enter reason for holiday request"
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-md ${formErrors.reason ? 'border-red-500' : 'border-neutral-300'}`}
                />
                {formErrors.reason && <p className="mt-1 text-sm text-red-600">{formErrors.reason}</p>}
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Schedule leave More popup */}
      {scheduleMorePopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md shadow-xl max-h-[85vh] flex flex-col">
            <div className="flex justify-between items-center border-b border-neutral-200 px-5 py-4 shrink-0">
              <h3 className="text-lg font-semibold text-neutral-900">
                Schedule leave — {formatCompanyDate(scheduleMorePopup)}
              </h3>
              <button
                type="button"
                onClick={() => setScheduleMorePopup(null)}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto space-y-2">
              {(scheduleCoverageByDate[scheduleMorePopup] || []).length === 0 ? (
                <p className="text-sm text-neutral-500">No schedule leave for this day.</p>
              ) : (
                (scheduleCoverageByDate[scheduleMorePopup] || []).map((s) => (
                  <div
                    key={s.scheduleId}
                    className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-neutral-900 inline-flex items-center gap-1.5">
                        <CalendarDays className="h-4 w-4 text-neutral-500" />
                        {s.scheduleName}
                      </span>
                      <span className="tabular-nums font-semibold text-neutral-800">{s.count}</span>
                    </div>
                    <div className="mt-1 text-xs text-neutral-600">
                      {s.approved} approved · {s.pending} pending
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Status Details Popup */}
      {statusPopup && (() => {
        const holidays = statusPopup === 'Pending' ? pendingHolidays
          : statusPopup === 'Approved' ? approvedHolidays
          : statusPopup === 'Denied' ? deniedHolidays
          : cancelledHolidays;
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-lg max-h-[85vh] flex flex-col shadow-xl">
              <div className="flex justify-between items-center border-b border-neutral-200 px-6 py-4 shrink-0">
                <h2 className="text-lg font-semibold text-neutral-800">{statusPopup} Holiday Requests</h2>
                <button
                  onClick={() => setStatusPopup(null)}
                  className="text-neutral-400 hover:text-neutral-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-4">
                {holidays.length === 0 ? (
                  <p className="text-neutral-500 text-center py-8">No {statusPopup.toLowerCase()} holiday requests.</p>
                ) : (
                  <div className="divide-y divide-neutral-200 space-y-0">
                    {holidays
                      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                      .map(holiday => {
                        const startDate = new Date(holiday.startDate);
                        const endDate = new Date(holiday.endDate);
                        const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                        return (
                          <div key={holiday.id} className="py-4 border-l-4 border-blue-500 pl-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-semibold text-neutral-900">
                                    {formatCompanyDate(startDate)}
                                    {daysDiff > 1 && ` - ${formatCompanyDate(endDate)}`}
                                  </span>
                                </div>
                                <div className="mt-1 text-sm text-neutral-600 flex items-center gap-2 flex-wrap">
                                  {daysDiff} day{daysDiff > 1 ? 's' : ''}
                                  {holiday.totalHours != null && holiday.totalHours > 0 && (
                                    <span>· {holiday.totalHours}h</span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => { setStatusPopup(null); setDetailsPopupHoliday(holiday); }}
                                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:underline font-medium"
                                  >
                                    <Info className="h-3.5 w-3.5" />
                                    Holiday type details
                                  </button>
                                </div>
                                {holiday.reason && <p className="mt-2 text-sm text-neutral-700 italic">{holiday.reason}</p>}
                                <div className="mt-2 text-sm text-neutral-600">
                                  <span className="font-medium">Manager/Supervisor:</span> {holiday.managerSupervisor}
                                </div>
                                <div className="text-sm text-neutral-600">
                                  <span className="font-medium">Venue/Department:</span> {holiday.venueDepartment}
                                </div>
                                {holiday.rejectionReason && (
                                  <div className="mt-2 text-sm text-red-600">
                                    <span className="font-medium">Rejection Reason:</span> {holiday.rejectionReason}
                                  </div>
                                )}
                              </div>
                              {holiday.status === 'Pending' && (
                                <button
                                  onClick={() => handleCancelHoliday(holiday)}
                                  className="px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg shrink-0"
                                >
                                  Cancel Holiday
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
              <div className="px-6 py-4 border-t border-neutral-200 flex justify-end shrink-0">
                <button
                  onClick={() => setStatusPopup(null)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Holiday Type Details Popup */}
      {detailsPopupHoliday && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md shadow-xl">
            <div className="flex justify-between items-center border-b border-neutral-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-neutral-800">Holiday Type Details</h2>
              <button
                onClick={() => setDetailsPopupHoliday(null)}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-neutral-600 mb-4">
                {formatCompanyDate(detailsPopupHoliday.startDate)}
                {detailsPopupHoliday.startDate !== detailsPopupHoliday.endDate && (
                  <> – {formatCompanyDate(detailsPopupHoliday.endDate)}</>
                )}
              </p>
              {detailsPopupHoliday.holidayTypes && detailsPopupHoliday.holidayTypes.length > 0 ? (
                <div className="space-y-2">
                  {[...detailsPopupHoliday.holidayTypes]
                    .sort((a, b) => a.date.localeCompare(b.date))
                    .map(({ date, holidayType }) => (
                      <div key={date} className="flex justify-between items-center py-2 px-3 bg-neutral-50 rounded-md">
                        <span className="text-sm font-medium text-neutral-800">
                          {formatCompanyDateLocale(date + 'T00:00:00', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                        <span className="text-sm font-medium px-2 py-1 rounded bg-blue-100 text-blue-800">
                          {holidayType}
                        </span>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-neutral-500 italic">No holiday type details available for this request.</p>
              )}
              {leaveBalanceCards.length > 0 && (
                <div className="mt-4 pt-4 border-t border-neutral-200">
                  <h3 className="text-sm font-semibold text-neutral-800 mb-2">Your Leave Balances</h3>
                  <div className="space-y-2">
                    {leaveBalanceCards.map(lt => (
                      <div key={lt.id} className="flex justify-between items-center py-2 px-3 bg-neutral-50 rounded-md text-sm gap-3">
                        <span className="font-medium text-neutral-800">{lt.name}</span>
                        <span className="text-neutral-600 flex flex-wrap gap-3 justify-end">
                          <span><span className="text-neutral-700 font-medium">{lt.accrued}</span> accrued</span>
                          <span><span className="text-orange-600 font-medium">{lt.used}</span> used</span>
                          <span><span className="text-amber-600 font-medium">{lt.pending}</span> pending</span>
                          <span><span className="text-blue-600 font-medium">{lt.available}</span> available</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-neutral-200 flex justify-end">
              <button
                onClick={() => setDetailsPopupHoliday(null)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyHolidays;
